import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { WorkflowRuleActionEdit } from '../../workflow-rule-action-edit-implemention';
import {
  ChannelType,
  ChannelUIds,
  ChannelTypeIcons,
  ConfiguredChannel,
  Template,
  EverbridgeTemplate,
  MassCommsContactDataset
} from 'src/app/modules/management/store/communication-center/communications.model';
import * as fromSharing from 'src/app/modules/management/store/communication-center';
import { UserProfileApiService } from 'src/app/modules/core/store/user-profile';
import { TranslateService } from '@ngx-translate/core';
import { NotificationRuleConfiguration } from './notification-edit.model';
import { QuillEditorService } from 'src/app/modules/core/services/quill/quill.service';
import { WorkflowRuleDataField } from 'src/app/modules/communications/store/workflow/workflow.models';
import { QueryParams, Workflow, WorkflowStep, WorkflowStepRule, WorkflowStepRuleAction } from 'src/app/modules/core/store/models';
import { MassNotificationTemplate, MessageSettings, ToRecipientSettings } from 'src/app/modules/communications/store/communications';
import { CommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import { cloneDeep } from 'lodash-es';
import moment from 'moment';
import { TemplateDesignerService } from 'src/app/modules/communications/services/template-designer.service';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { ChannelSelectionService } from 'src/app/modules/communications/services/channel-selection-service';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'topo-notification-rule-edit',
    templateUrl: './notification-edit.component.html',
    styleUrls: ['./notification-edit.component.scss'],
    standalone: false
})
export class NotificationRuleEditComponent implements OnInit, OnDestroy, WorkflowRuleActionEdit {
  _readOnly: boolean;

  @Input() schema: WorkflowRuleDataField[];
  @Input() workflow: Workflow;
  @Input() step: WorkflowStep;
  @Input() rule: WorkflowStepRule;
  @Input() implementationUId: string;
  @Input() set readOnly(value: boolean) {
    this._readOnly = value;
    if (value === true) {
      this.selectedTemplateUId = null;
      this.showTemplates = false;
      this.showNotificationMessage = false;
      this.showSmsNotificationMessage = false;
      this.showRecipientSelect = false;
    }
  }
  @Input() action: WorkflowStepRuleAction;

  destroy$: Subject<void> = new Subject();
  templates: MassNotificationTemplate[] = [];
  selectedTemplate: MassNotificationTemplate;
  selectedTemplateUId: string;

  showTemplates: boolean = false;
  showNotificationMessage: boolean = false;
  showSmsNotificationMessage: boolean = false;
  editor: any;
  organizationUId: string;
  showRecipientSelect: boolean;
  showSubjectInput: boolean;
  selectedSubject: string;
  recipientSettings?: ToRecipientSettings;
  messageSettings?: MessageSettings;
  notificationMessage?: string;
  smsNotificationMessage?: string;

  constructor(
    private translationService: TranslateService,
    private notify: SystemNotificationsService,
    private userProfileApiService: UserProfileApiService,
    private communicationsService: CommunicationsApiService,
    private templateDesignerService: TemplateDesignerService,
    private channelSelectionService: ChannelSelectionService,
    private editorService: QuillEditorService,
    private cultureService: CultureService
  ) {}

  get editorModules() {
    this.editorService.init();
    return {
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'font': [] }],
          [{ 'align': [] }],
          ['blockquote'],
          ['link'],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ redo: 'redo' }, { undo: 'undo' }]
        ],
        handlers: {
          redo: () => {
            this.editor?.history.redo();
          },
          undo: () => {
            this.editor?.history.undo();
          }
        }
      },
      history: { delay: 2000, maxStack: 500, userOnly: true },
      mention: {
        minChars: 1,
        mentionDenotationChars: ['@'],
        source: (searchTerm, renderList) => {
          this.getUserList(searchTerm, renderList);
        }
      }
    };
  }

  getUserList(searchText, renderList) {
    this.userProfileApiService.getUsersForMention(this.organizationUId, searchText).subscribe(res => {
      const entries = res.data.map(p => ({ id: p.uId, value: `${p.displayName}` }));
      renderList(entries);
    });
  }

  editorCreated(editor: any) {
    this.editor = editor;
    this.editor.focus();
  }

  load(): void {
    this.showRecipientSelect = false;
    this.showTemplates = true;
    this.showNotificationMessage = false;
    this.showSmsNotificationMessage = false;
    this.selectedTemplateUId = null;

    this.templates = [];
    this.templates.push({
      uId: null,
      name: this.translationService.instant('MANAGEMENT.DATASETS.LOADING')
    } as MassNotificationTemplate);

    const settings = this.action?.configuration ? (JSON.parse(this.action.configuration) as NotificationRuleConfiguration) : null;

    this.recipientSettings = settings?.recipientSettings;
    this.messageSettings = settings?.messageSettings;
    this.notificationMessage = this.templateDesignerService.getTranslateText(
      this.messageSettings?.notificationMessage,
      this.cultureService?.currentLang
    );
    this.smsNotificationMessage = this.templateDesignerService.getTranslateText(
      this.messageSettings?.smsNotificationMessage,
      this.cultureService?.currentLang
    );

    const params: QueryParams = {
      skip: 0,
      take: 1000,
      where: `IsActive=true`,
      sortField: 'Name',
      sortDirection: 'Ascending'
    };

    this.communicationsService.getMassNotificationTemplates(params, false, true).subscribe(res => {
      // include only templates that not using the current workflow, to prevent recursive execution
      this.templates = res?.data?.filter(t => t.massNotificationWorkflowUId !== this.workflow.uId);

      this.selectedTemplateUId = settings?.templateUId;
      if (!!!this.selectedTemplateUId && this.templates?.length > 0) {
        this.selectedTemplateUId = this.templates[0].uId;
      }
      this.loadTemplateData();
    });
  }

  selectedRecipientsChanged(settings: ToRecipientSettings): void {
    if (!!!this.recipientSettings) {
      this.recipientSettings = {};
    }
    this.recipientSettings.recipients = settings.recipients;
    this.recipientSettings.datasetRecipients = [];
    settings.datasetRecipients?.forEach(r => {
      this.recipientSettings.datasetRecipients.push(r);
    });
  }

  templateSelected(): void {
    this.recipientSettings = null;
    this.messageSettings = null;
    this.notificationMessage = null;
    this.smsNotificationMessage = null;
    this.loadTemplateData();
  }

  private loadTemplateData(): void {
    this.communicationsService.getMassNotificationTemplate(this.selectedTemplateUId).subscribe(template => {
      this.selectedTemplate = template;

      if (!!!this.recipientSettings) {
        this.recipientSettings = cloneDeep(this.selectedTemplate?.recipientSettings);
      }
      if (!!!this.messageSettings) {
        this.messageSettings = cloneDeep(this.selectedTemplate?.defaultMessageSettings);
      }
      if (!!!this.notificationMessage) {
        this.notificationMessage = this.templateDesignerService.getTranslateText(
          this.messageSettings?.notificationMessage,
          this.cultureService?.currentLang
        );
      }
      if (!!!this.smsNotificationMessage) {
        this.smsNotificationMessage = this.templateDesignerService.getTranslateText(
          this.messageSettings?.smsNotificationMessage,
          this.cultureService?.currentLang
        );
      }

      this.showSmsNotificationMessage = this.selectedTemplate?.communicationTemplates?.some(
        t =>
          t.channelUId === ChannelUIds.TwilioChannelUId ||
          t.channelUId === ChannelUIds.HorizonSmsChannelUId ||
          t.channelUId === ChannelUIds.WhatsAppChannelUId
      );

      if (!!this.selectedTemplate) {
        this.showRecipientSelect = true;
      }
      this.showTemplates = true;
      this.showNotificationMessage = true;
    });
  }

  onMessageChanged(event: any) {
    if (!!!this.messageSettings) {
      this.messageSettings = {};
    }
    if (!!!this.messageSettings.notificationMessage) {
      this.messageSettings.notificationMessage = {};
    }
    this.messageSettings.notificationMessage[this.cultureService.currentLang] = event.html;
  }

  smsNotificationMessageChanged(event: any) {
    if (!!!this.messageSettings) {
      this.messageSettings = {};
    }
    if (!!!this.messageSettings.smsNotificationMessage) {
      this.messageSettings.smsNotificationMessage = {};
    }
    this.messageSettings.smsNotificationMessage[this.cultureService.currentLang] = event.srcElement?.value;
  }

  private getDisplayText(): string {
    let desc = this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-DESC-NOTIFICATION');
    desc += ` ${this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-SELECT-TEMPLATE')} <b>${this.selectedTemplate?.name}</b>`;
    return desc;
  }

  validate(): boolean {
    if (this.selectedTemplate === null) {
      this.notify.error(this.translationService.instant('MAP.ERROR-TEMPLATE-REQUIRED'));
      return false;
    }

    let totalRecipients = this.recipientSettings?.recipients?.length ?? 0;
    this.recipientSettings?.datasetRecipients?.map(r => (totalRecipients += r?.documentCount));

    if (!(totalRecipients > 0)) {
      const needRecipients = this.anyChannelNeedsRecipients();
      if (needRecipients) {
        this.notify.error(this.translationService.instant('MAP.ERROR-COMMUNICATION-RECIPIENTS-REQUIRED'));
        return false;
      }
    }

    return true;
  }

  private getNewNotificationName(): string {
    const rulePrefix = this.translationService.instant('COMMUNICATIONS.NOTIFICATION-RULE-PREFIX');
    return `${rulePrefix} ${moment().format('YYYY-MM-DD HH:mm')} ${this.selectedTemplate?.name}`;
  }

  private anyChannelNeedsRecipients(): boolean {
    const channelsCascadeSettings = this.selectedTemplate?.channelsCascadeSettings;
    if (!!!channelsCascadeSettings?.channelNotifications) {
      return false;
    }
    for (var group of channelsCascadeSettings?.channelNotifications) {
      //      for (var uid of group?.configuredChannelUIds) {
      const configuredChannel = this.channelSelectionService.getConfiguredChannel(group.configuredChannelUId);
      // slack and msteams are only one that may not need recipients
      if (
        configuredChannel?.channelUId !== ChannelUIds.SlackChannelUId &&
        configuredChannel?.channelUId !== ChannelUIds.MicrosoftTeamsChannelUId
      ) {
        return true;
      }
      if (configuredChannel?.settings?.sendDirectMessages === true) {
        return true;
      }
      //      }
    }
    return false;
  }

  save(): boolean {
    if (!this.validate()) {
      return false;
    }

    let settings = {
      namePrefix: this.translationService.instant('COMMUNICATIONS.NOTIFICATION-RULE-PREFIX'),
      templateUId: this.selectedTemplateUId,
      messageSettings: this.messageSettings,
      recipientSettings: this.recipientSettings
    } as NotificationRuleConfiguration;

    this.action.configuration = JSON.stringify(settings);
    this.action.displayText = this.getDisplayText();

    return true;
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
